import React from 'react';
import classNames from 'classnames';
import { kebabCase } from 'lodash/fp';
import { useDispatch } from 'react-redux';

// Components
import Icon from '../../../components/icons/Icon';
import Button from '../../../components/buttons/Button';
import FormattingOptionsPopup from './FormattingOptionsPopup';

// Manager
import { useTranslator } from '../../../components/i18n/useTranslator';
import { useActiveTableCallback, useActiveTableSelector } from '../store/hotTableStoreHooks';
import { selectActiveTableCellSelection } from '../store/selectors/activeHotTableSelectors';
import { updateTableCellType } from '../actions/hotTableActions';

// Utils
import { cellTypeUpdateShouldHappen } from '../utils/tableCellTypeUtils';
import { getNormalisedComputedCellValueFromHot } from '../utils/tableCellRenderValueUtils';
import { getAllCellsBetween } from '../utils/tableCellSelectionUtils';
import { translateCellType } from '../../../../common/table/cellTypeTranslations';
import { updateActiveHotTableCellTypeFormat } from '../utils/hotInstanceUtils/activeHotTableMutationUtils';

// Constants
import { CellTypeConstants } from '../../../../common/table/CellTypeConstants';
import { CellTypeObject } from '../../../../common/table/TableTypes';

// Styles
import './CellTypePopup.scss';

type CellTypeButtonProps = {
    currentCellType: keyof typeof CellTypeConstants;
    dispatchUpdateCellType?: (elementId: string, cellType: string) => void;
    isActive: boolean;
    allSelectedCells: Array<{ row: number; col: number }>;
    lastClickedType: string;
    setLastClickedType: (type: string) => void;
    locale: string;
};

const CellTypeButton: React.FC<CellTypeButtonProps> = (props) => {
    const { currentCellType, isActive, allSelectedCells, lastClickedType, setLastClickedType, locale } = props;
    const { icon, formattingOptions } = CellTypeConstants[currentCellType];

    const translate = useTranslator('popup', 'table');

    // @ts-ignore Couldn't figure out the type here
    const displayName = translateCellType(currentCellType, translate);

    // Does type have formatting options AND
    // do all the selected cells have the current cell type, OR is it the last clicked cell type?
    const showFormattingOptions = !!formattingOptions && (isActive || lastClickedType === currentCellType);

    const isDisabled = useActiveTableSelector((activeTable) => {
        if (!activeTable) return true;

        const activeTableInstance = activeTable.instance;

        return !allSelectedCells.some(({ row, col }) => {
            const normalisedComputedCellValue = getNormalisedComputedCellValueFromHot(
                activeTable.elementId,
                row,
                col,
                activeTableInstance,
            );
            return cellTypeUpdateShouldHappen(normalisedComputedCellValue, currentCellType, locale);
        });
    });

    const dispatch = useDispatch();

    const updateCellType = useActiveTableCallback(
        (activeTable) => {
            dispatch(
                updateTableCellType({
                    hotInstance: activeTable.instance,
                    session: activeTable.session,
                    elementId: activeTable.elementId,
                    cellType: currentCellType,
                }),
            );
            setLastClickedType(currentCellType);
        },
        [currentCellType],
    );

    const updateCellTypeFormat = useActiveTableCallback(
        (activeTable, typeFormatChanges: CellTypeObject) =>
            updateActiveHotTableCellTypeFormat({
                hotInstance: activeTable.instance,
                session: activeTable.session,
                typeFormatChanges,
            }),
        [],
    );

    return (
        <>
            <Button
                onClick={updateCellType}
                className={classNames('CellTypeButton', kebabCase(currentCellType), {
                    active: isActive,
                    disabled: isDisabled,
                })}
            >
                <Icon name={icon} />
                <span>{displayName}</span>
                {isActive && <Icon name={'block-style-tick'} />}
                {!isActive && !!formattingOptions && <Icon name="chevron-right" />}
            </Button>
            {showFormattingOptions && (
                <FormattingOptionsPopup
                    {...props}
                    showFormattingOptions={showFormattingOptions}
                    updateCellTypeFormat={updateCellTypeFormat}
                />
            )}
        </>
    );
};

type CellTypePopupProps = Omit<CellTypeButtonProps, 'allSelectedCells' | 'currentCellType' | 'isActive'> & {
    activeCellType: string;
    closePopup: (() => void) | null;
};

const CellTypePopup: React.FC<CellTypePopupProps> = (props) => {
    const { activeCellType } = props;
    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);
    const allSelectedCells = getAllCellsBetween(currentCellSelections);

    return (
        <>
            {Object.keys(CellTypeConstants).map((currentCellType) => (
                <CellTypeButton
                    {...props}
                    allSelectedCells={allSelectedCells}
                    key={currentCellType}
                    currentCellType={currentCellType as keyof typeof CellTypeConstants}
                    isActive={activeCellType === currentCellType}
                />
            ))}
        </>
    );
};

export default CellTypePopup;
